/**
 * Custom Product Options - Complete Enhanced Functionality
 * Version 2.0 - Updated for button-based symbols
 */

(function () {
  "use strict";

  class CustomProductOptions {
    constructor() {
      this.config = {
        maxCharacters: window.engravingLimit, // Configurable max characters
      };

      this.symbolMap = {
        front: {},
        back: {},
      };
      this.symbolMapSingle = {
      };

      this.currentBasePrice = 0;

      this.init();
    }

    init() {
      if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", () => this.setup());
      } else {
        this.setup();
      }
    }

    setup() {
      console.log("🚀 Custom Product Options Initializing...");

      // Feature 1: Radio button unselect for Birthstone-like radios.
      // setupStoneUnselect() handles EVERY [data-stone] block (original Birthstone
      // + all per-side Birthstones), each with its own lastSelected pointer.
      // The older setupRadioUnselect() is kept for reference but not wired in —
      // it used to target only [data-option="Birthstone"] and conflicted with
      // setupStoneUnselect() by racing over the same radios.
      this.setupStoneUnselect();

      // Feature 2: Back side toggle functionality
      this.setupBackSideToggle();

      // Feature 2.5: Toggle Custom Size input visibility
      this.setupCustomSizeToggle();

      // Feature 3: Required validation with highlighting
      this.setupRequiredValidation();

      // Feature 4: Symbol buttons with image preview
      this.setupSymbolButtons();
      this.setupSymbolsBlocks();
      this.displayBirthstoneBar();
      this.displayBirthstoneBarStones();

      // Feature 4.5: Sync Engraving Side checkboxes with hidden option
      this.setupEngravingSideSync();

      // Feature 5: Selected swatch display for all inputs
      this.setupSelectedSwatchDisplay();

      // Feature 6: Hijack Add to Cart to push EVERY properties[...] value (including the file input)
      this.setupAddToCartHijack();

      // Feature 7: Dynamic Price Update
      this.setupPriceObserver();

      this.imageUpload();

      this.personalisation();

      this.isolatedPersonalisation();

      // Feature 8: State + Initials picker (necklace)
      this.setupStateSelection();

      // Feature 9: Progressive sides selection (Front → Right → Back → Left)
      this.displaySidesBlocks();

      console.log("✅ Custom Product Options Ready");
    }

    /**
     * Feature 1: Allow radio buttons to be unselected on second click
     */
    setupRadioUnselect() {
      const birthstoneContainer = document.querySelector('[data-option="Birthstone"]');
      if (!birthstoneContainer) return;
      const radioInputs = birthstoneContainer.querySelectorAll('input[type="radio"]');
      let lastSelected = null;
      radioInputs.forEach((radio) => {
        radio.addEventListener("click", (e) => {
          if (lastSelected === radio && radio.checked) {
            radio.checked = false;
            lastSelected = null;
            radio.dispatchEvent(new Event("change", { bubbles: true }));
            console.log("🔘 Birthstone unselected");
          } else {
            lastSelected = radio;
          }
        });
      });

      console.log("✅ Radio unselect enabled");
    }

    /**
     * Feature 2: Toggle Back Symbol and Back Engraving visibility
     */
    setupBackSideToggle() {
      const backSideCheckbox = document.querySelector('.shopify-product-form input[value="Front Side + Back Side"]');
      if (!backSideCheckbox) return;

      const backSymbolSection = document.querySelector('[data-option="Back engraving symbol"]');
      const backEngravingSection = document.querySelector('[data-option="Back engraving"]');

      const toggleBackSections = (show) => {
        if (show) {
          backSymbolSection?.classList.remove("hide");
          backEngravingSection?.classList.remove("hide");
        } else {
          backSymbolSection?.classList.add("hide");
          backEngravingSection?.classList.add("hide");
          const input = backEngravingSection?.querySelector('input[name="properties[Back engraving]"]');
          if (input) {
            input.value = "";
            input.dispatchEvent(new Event("input", { bubbles: true }));
          }
        }
      };

      toggleBackSections(backSideCheckbox.checked);

      backSideCheckbox.addEventListener("change", (e) => {
        toggleBackSections(e.target.checked);
        this.updatePriceDisplay();
      });

      console.log("✅ Back side toggle enabled");
    }

    /**
     * Feature 2.5: Toggle Custom Size input visibility
     */
    setupCustomSizeToggle() {
      const sizeRadios = document.querySelectorAll('input[name="properties[Size]"]');
      if (!sizeRadios.length) return;

      const customSizeSection = document.querySelector('[data-option="Custom size"]');
      if (!customSizeSection) return;

      const toggleCustomSizeSection = (show) => {
        if (show) {
          customSizeSection.classList.remove("hide");
        } else {
          customSizeSection.classList.add("hide");
          const input = customSizeSection.querySelector('input[name="properties[Custom size]"]');
          if (input) {
            input.value = "";
            input.dispatchEvent(new Event("input", { bubbles: true }));
          }
        }
      };

      const updateVisibility = () => {
        const selectedRadio = document.querySelector('input[name="properties[Size]"]:checked');
        toggleCustomSizeSection(selectedRadio && selectedRadio.value === "Custom Size");
      };

      // Initial check
      updateVisibility();

      sizeRadios.forEach(radio => {
        radio.addEventListener("change", updateVisibility);
      });

      console.log("✅ Custom size toggle enabled");
    }

    /**
     * Feature 3: Required validation with button state
     */
    setupRequiredValidation() {
      const formContainer = document.querySelector("div.shopify-product-form");
      const form_id = formContainer?.dataset.form_id;
      const form = document.getElementById(form_id);
      if (!form || !formContainer) return;

      const submitButton = form.querySelector('button[type="submit"][name="add"]');
      if (!submitButton) return;
      submitButton.classList.add("required-fields");

      const HEADER_OFFSET = 70;

      /**
       * Static required fields. Each field may define:
       *   selector  — CSS selector for the input(s)
       *   type      — "radio" | "text" | "file"
       *   label     — human-readable label (used for logs)
       *   when()    — optional predicate; field is only required if it returns true
       *   extraClass — optional class added to the container on error
       */
      const staticFields = [
        { selector: 'input[name="properties[Font]"]',            type: "radio", label: "Font" },
        { selector: 'input[name="properties[Size]"]',            type: "radio", label: "Select Size" },
        { selector: 'input[name="properties[Birthstone]"]',      type: "radio", label: "Birthstone" },
        { selector: 'input[name="properties[Front engraving]"]', type: "text",  label: "Front engraving" },
        {
          selector: 'input[name="properties[Back engraving]"]',
          type: "text",
          label: "Back engraving",
          when: () => {
  const cb = document.querySelector('#sides_picker_back');
  return !!(cb && cb.checked);
},
        },
        {
          selector: 'input[name="properties[Custom size]"]',
          type: "text",
          label: "Custom size",
          when: () => {
            const r = document.querySelector('input[type="radio"][name="properties[Size]"][value="Custom Size"]');
            return !!(r && r.checked);
          },
        },
        // --- new fields ---
        { selector: 'input[name="properties[Image]"]',           type: "file", label: "Image" },
        {
          selector: '[data-option="Personalization"] input[name="properties[Personalization]"]',
          type: "text",
          label: "Personalization",
          when: () => !!document.querySelector('[data-option="Personalization"]'),
        },
        { selector: "#co_state_1",                               type: "text", label: "State 1" },
        { selector: "#co_state_2",                               type: "text", label: "State 2" },
        { selector: "#co_state_initials",                        type: "text", label: "Initials" },
        { selector: 'input[name="properties[Dog Breed]"]',       type: "radio", label: "Dog Breed" },
        { selector: 'input[name="properties[Dog Name]"]',       type: "text",  label: "Dog Name" },
        {
          selector: 'input[name="properties[Engraving]"]',
          type: "text",
          label: "Engraving",
          when: () => {
            const stonesRoot = document.querySelector('[data-option="Stones"]');
            if (!stonesRoot) return false;
            return !!stonesRoot.querySelector(".custom-options--block:not(.custom-options--block-blocked)");
          },
        },
        { selector: 'input[name="properties[Right Engraving]"]', type: "text", label: "Right engraving",
  when: () => {
    const cb = document.querySelector('#sides_picker_right');
    return !!(cb && cb.checked);
  }
},
        { selector: 'input[name="properties[Left Engraving]"]', type: "text", label: "Left engraving",
  when: () => {
    const cb = document.querySelector('#sides_picker_left');
    return !!(cb && cb.checked);
  }
},
      ];

      /**
       * Dynamic fields that depend on current state of the UI.
       * For Sides: each active pill contributes its engraving (+ birthstone, if present).
       */
      const getDynamicFields = () => {
        const out = [];
        const sidesContainer = document.querySelector(".sides-selection");
        if (!sidesContainer) return out;
        const pills = sidesContainer.querySelectorAll(".sides-picker__checkbox");
        pills.forEach((cb) => {
          if (!cb.checked) return;
          const label = (cb.dataset.sideLabel || "").replace(/\s*Side$/i, "").trim();
          if (!label) return;
          out.push({
            selector: `input[name="properties[${label} Engraving]"]`,
            type: "text",
            label: `${label} Engraving`,
          });
          out.push({
            selector: `input[name="properties[${label} Birthstone]"]`,
            type: "radio",
            label: `${label} Birthstone`,
          });
        });
        return out;
      };

      /**
       * Charms: only unblocked blocks count; engraving line item must exist (metafield gated).
       */
      const getDynamicCharmFields = () => {
        const out = [];
        const charmBlocks = document.querySelectorAll("[data-option='Charms'] .custom-options--block");
        charmBlocks.forEach((block) => {
          if (block.classList.contains("custom-options--block-blocked")) return;
          const idx = block.dataset.index;
          if (!idx) return;
          const engrInput = block.querySelector(`input[name="properties[Charm ${idx} engraving]"]`);
          if (engrInput) {
            out.push({
              selector: `input[name="properties[Charm ${idx} engraving]"]`,
              type: "text",
              label: `Charm ${idx} engraving`,
            });
          }
          if (block.querySelector(`input[name="properties[Birthstone Charm ${idx}]"]`)) {
            out.push({
              selector: `input[name="properties[Birthstone Charm ${idx}]"]`,
              type: "radio",
              label: `Birthstone Charm ${idx}`,
            });
          }
        });
        return out;
      };

      /**
       * Bar necklace / multi-stone: each unblocked block under [data-option="Stones"]
       * requires properties[Birthstone N] (N matches variant slot / data-index).
       */
      const getDynamicStoneBirthstoneFields = () => {
        const out = [];
        const stoneBlocks = document.querySelectorAll("[data-option='Stones'] .custom-options--block");
        stoneBlocks.forEach((block) => {
          if (block.classList.contains("custom-options--block-blocked")) return;
          const idx = block.dataset.index;
          if (!idx) return;
          const name = `properties[Birthstone ${idx}]`;
          if (block.querySelector(`input[name="${name}"]`)) {
            out.push({
              selector: `input[name="${name}"]`,
              type: "radio",
              label: `Birthstone ${idx}`,
            });
          }
        });
        return out;
      };

      const getAllFields = () => [
        ...staticFields,
        ...getDynamicFields(),
        ...getDynamicCharmFields(),
        ...getDynamicStoneBirthstoneFields(),
      ];

      const resolveContainer = (field) => {
        const input = document.querySelector(field.selector);
        if (!input) return null;
        return (
          input.closest(".custom-options__engraving-input") ||
          input.closest(".custom-options--stack") ||
          input.closest(".custom-options--upload") ||
          input.closest(".custom-options--block") ||
          input.parentElement
        );
      };

      const isFieldValid = (field) => {
        const inputs = document.querySelectorAll(field.selector);
        if (!inputs.length) return true; // field not rendered on this product
        if (field.when && !field.when()) return true; // not required in current state

        if (field.type === "radio") {
          return Array.from(inputs).some((i) => i.checked);
        }
        if (field.type === "file") {
          return !!(inputs[0] && inputs[0].files && inputs[0].files.length > 0);
        }
        return Array.from(inputs).every((i) => i.value.trim() !== "");
      };

      const scrollToFirstError = () => {
        const firstError = document.querySelector(
          ".custom-options--stack.error, .custom-options__engraving-input.error, .custom-options--block.error, .custom-options--upload.error",
        );
        if (!firstError) return;
        const elementPosition = firstError.getBoundingClientRect().top + window.scrollY;
        window.scrollTo({ top: elementPosition - HEADER_OFFSET, behavior: "smooth" });
      };

      const applyFieldErrorsAndGetValid = () => {
        let allValid = true;
        getAllFields().forEach((field) => {
          const valid = isFieldValid(field);
          const container = resolveContainer(field);

          if (!valid) {
            allValid = false;
            if (container) {
              container.classList.add("error");
              if (field.extraClass) container.classList.add(field.extraClass);
              if (field.selector === "#co_state_1" || field.selector === "#co_state_2") {
                const slotIdx = field.selector === "#co_state_1" ? 1 : 2;
                document.querySelector(`.state-slot[data-slot="${slotIdx}"]`)?.classList.add("error");
              }
            }
          } else if (container) {
            container.classList.remove("error");
            if (field.extraClass) container.classList.remove(field.extraClass);
            if (field.selector === "#co_state_1" || field.selector === "#co_state_2") {
              const slotIdx = field.selector === "#co_state_1" ? 1 : 2;
              document.querySelector(`.state-slot[data-slot="${slotIdx}"]`)?.classList.remove("error");
            }
          }
        });
        return allValid;
      };

      this._necklaceApplyFieldErrorsAndGetValid = applyFieldErrorsAndGetValid;
      this._necklaceScrollToFirstFormError = () => scrollToFirstError();

      // Silent recalculation — toggles the submit button's guard class only
      const recalcButtonState = () => {
        const allValid = getAllFields().every(isFieldValid);
        if (allValid) submitButton.classList.remove("required-fields");
        else submitButton.classList.add("required-fields");
      };

      // Full validation: paints errors, scrolls to the first one, auto-submits if all ok
      const validateSubmit = () => {
        const allValid = applyFieldErrorsAndGetValid();

        setTimeout(() => {
          if (allValid && submitButton.classList.contains("required-fields")) {
            submitButton.classList.remove("required-fields");
            submitButton.click(); // proceeds through the hijack / theme default submit
          } else if (!allValid) {
            submitButton.classList.add("required-fields");
            scrollToFirstError();
          }
        }, 50);
      };

      submitButton.addEventListener("click", (event) => {
        if (submitButton.classList.contains("required-fields")) event.preventDefault();
        validateSubmit();
      });

      // --- delegated cleanup listeners ---
      // We listen on .shopify-product-form (the wrapping DIV) instead of the
      // actual <form> element, because hidden inputs linked via the `form=""`
      // attribute (e.g. #co_state_1, #co_state_2, properties[Image]) live in the
      // DOM under .shopify-product-form but are NOT descendants of <form>, so
      // change/input events never bubble to <form>.
      const clearErrorFor = (input) => {
        input
          .closest(
            ".custom-options--stack.error, .custom-options__engraving-input.error, .custom-options--block.error, .custom-options--upload.error",
          )
          ?.classList.remove("error");
      };

      formContainer.addEventListener("input", (e) => {
        const t = e.target;
        if (!t || !t.name || !t.name.startsWith("properties[")) return;
        clearErrorFor(t);
        recalcButtonState();
      });
      formContainer.addEventListener("change", (e) => {
        const t = e.target;
        if (!t || !t.name) return;
        if (t.name.startsWith("properties[")) {
          clearErrorFor(t);
        }
        recalcButtonState();
      });

      document.addEventListener("change", (e) => {
        const t = e.target;
        if (!t || t.type !== "radio") return;
        if (!t.closest(".option-how-many-charms, .option-how-many-stones")) return;
        recalcButtonState();
      });

      // Clear the state-slot red highlight as soon as the user picks a state / toggles a side
      document.addEventListener("click", (e) => {
        const isStateOrSide =
          e.target.closest(".state-option") ||
          e.target.closest(".sides-picker__pill") ||
          e.target.closest(".state-slot");
        if (!isStateOrSide) return;
        setTimeout(() => {
          document.querySelectorAll(".state-slot.error").forEach((s) => s.classList.remove("error"));
          // When both state slots are filled, the whole .state-selection block
          // might still carry the .error class — clear it too.
          const stateStack = document.querySelector(".state-selection.error");
          if (stateStack) {
            const s1 = stateStack.querySelector("#co_state_1");
            const s2 = stateStack.querySelector("#co_state_2");
            if (s1?.value.trim() && s2?.value.trim()) stateStack.classList.remove("error");
          }
          recalcButtonState();
        }, 20);
      });

      recalcButtonState();
    }

    /**
     * Feature 4: Symbol buttons with preview
     */
    setupSymbolButtons() {
      this.setupSymbolsForSide("Front engraving symbol", "Front engraving", "front");
      this.setupSymbolsForSide("Back engraving symbol", "Back engraving", "back");
    }
    

    setupSymbolsForSide(symbolOptionName, engravingOptionName, side) {
      const symbolSection = document.querySelector(`[data-option="${symbolOptionName}"]`);
      const engravingSection = document.querySelector(`[data-option="${engravingOptionName}"]`);
      const engravingInput = document.querySelector(`input[name="properties[${engravingOptionName}]"]`);

      if (!engravingInput || !engravingSection) return;

      // Create preview element after the input
      let previewElement = engravingSection.querySelector(".engraving-preview");
      if (!previewElement) {
        previewElement = document.createElement("div");
        previewElement.className = "engraving-preview";
        previewElement.style.cssText = `
          margin-top: 12px;
          padding: 12px;
          background: #f8f8f8;
          border: 1px solid #e0e0e0;
          border-radius: 6px;
          min-height: 50px;
          display: flex;
          align-items: center;
          gap: 6px;
          flex-wrap: wrap;
          font-size: 16px;
          line-height: 1.5;
        `;

        const inputContainer = engravingSection.querySelector(".custom-options--option--container");
        inputContainer.after(previewElement);
      }

      // Create character counter in help text
      let helpText = engravingSection.querySelector(".custom-options--helptext-content");
      if (!helpText) {
        helpText = document.createElement("div");
        helpText.className = "custom-options--helptext-content";
        engravingSection.appendChild(helpText);
      }

      const updateCharacterCount = () => {
        const count = this.getCharacterCount(side);
        helpText.innerHTML = `<p>${count}/${this.config.maxCharacters}</p>`;

        if (count >= this.config.maxCharacters) {
          helpText.style.color = "#d32f2f";
        } else {
          helpText.style.color = "";
        }
      };

      // Get all symbol buttons (section omitted when symbols hidden — still enforce limit + counter)
      const symbolButtons = symbolSection ? symbolSection.querySelectorAll("button[data-target]") : [];

      // Populate symbol map
      symbolButtons.forEach((button, index) => {
        const symbolImage = button.querySelector(".custom-options--option__image-value");
        const symbolName = button.querySelector(".co_option_value")?.textContent || `symbol-${index + 1}`;
        const imageUrl = symbolImage?.style.backgroundImage.match(/url\("?(.+?)"?\)/)?.[1];
        if (symbolName && imageUrl) {
          this.symbolMap[side][symbolName] = imageUrl;
        }
      });

      let lastValue = engravingInput.value;

      // Handle symbol button clicks
      symbolButtons.forEach((button, index) => {
        button.addEventListener("click", (e) => {
          e.preventDefault();

          const symbolName = button.querySelector(".co_option_value")?.textContent || `symbol-${index + 1}`;
         
          if (!this.symbolMap[side][symbolName]) return;

          // Get current cursor position
          const cursorPos = engravingInput.selectionStart || engravingInput.value.length;
          const currentValue = engravingInput.value;

          // Check character limit
          if (this.getCharacterCount(side) >= this.config.maxCharacters) {
            alert(`Maximum ${this.config.maxCharacters} characters reached`);
            return;
          }

          // Insert symbol tag at cursor position
          const symbolTag = `[${symbolName}]`;
          const before = currentValue.substring(0, cursorPos);
          const after = currentValue.substring(cursorPos);
          engravingInput.value = before + symbolTag + after;
          engravingInput.dispatchEvent(new Event("input", { bubbles: true }));

          // Update preview
          this.updatePreview(side, engravingInput, previewElement);
          updateCharacterCount();

          // Set cursor after symbol
          engravingInput.selectionStart = engravingInput.selectionEnd = cursorPos + symbolTag.length;
          engravingInput.focus();

          // console.log(`✨ Symbol added: ${symbolName}`);
        });
      });

      // Handle text input changes
      engravingInput.addEventListener("input", (e) => {
        const currentCount = this.getCharacterCount(side, engravingInput.value);
        const previousCount = this.getCharacterCount(side, lastValue);

        if (currentCount > this.config.maxCharacters && currentCount > previousCount) {
          engravingInput.value = lastValue;
        } else {
          lastValue = engravingInput.value;
        }

        this.updatePreview(side, engravingInput, previewElement);
        updateCharacterCount();
      });

      // Handle backspace to remove symbols
      engravingInput.addEventListener("keydown", (e) => {
        if (e.key === "Backspace") {
          const cursorPos = engravingInput.selectionStart;
          const currentValue = engravingInput.value;

          if (cursorPos > 0) {
            const beforeCursor = currentValue.substring(0, cursorPos);
            // Check if we are deleting the closing bracket of a symbol
            const match = beforeCursor.match(/\[([^\]]+)\]$/);

            if (match) {
              const symbolName = match[1];
              if (this.symbolMap[side][symbolName]) {
                e.preventDefault();
                // Remove the whole tag
                const newValue = currentValue.substring(0, cursorPos - match[0].length) + currentValue.substring(cursorPos);
                engravingInput.value = newValue;
                engravingInput.selectionStart = engravingInput.selectionEnd = cursorPos - match[0].length;
                engravingInput.dispatchEvent(new Event("input", { bubbles: true }));

                this.updatePreview(side, engravingInput, previewElement);
                updateCharacterCount();
              }
            }
          }
        }
      });

      // Initial update
      updateCharacterCount();

      console.log(`✅ Engraving ${symbolSection ? "+ symbols" : "(limit + counter only)"} for ${side}`);
    }

    updatePreview(side, input, previewElement) {
      const value = input.value.trimEnd(); // Trim trailing spaces
      previewElement.innerHTML = "";

      if (!value) {
        previewElement.style.display = "none";
        return;
      }

      previewElement.style.display = "flex";

      // Split by symbol tags
      const parts = value.split(/(\[[^\]]+\])/);

      let previewHTML = "";
      parts.forEach((part) => {
        const match = part.match(/^\[([^\]]+)\]$/);
        if (match && this.symbolMap[side][match[1]]) {
          const symbolName = match[1];
          const imageUrl = this.symbolMap[side][symbolName];
          previewHTML += `<img src="${imageUrl}" alt="${symbolName}" style="width: 24px; height: 24px; object-fit: contain; margin: 0 2px;">`;
        } else if (part) {
          previewHTML += `<span>${part}</span>`;
        }
      });

      previewElement.innerHTML = previewHTML;
    }

    /**
     Image upload
     */
    imageUpload(){
        //Drop and drag
        //selecting all required elements
            const dropArea = document.querySelector(".custom-options--upload-container");
            if (dropArea) {
                const button = dropArea.querySelector(".custom-options--upload-container-empty"),
                    input = dropArea.querySelector("input"),
                    removePhoto = dropArea.querySelector(".upload-image__remove"),
                    photoItem = dropArea.querySelector(".upload-image");

                let files;
                //let file; //this is a global variable and we'll use it inside multiple functions

                button.onclick = () => {
                    input.click(); //if user click on the button then the input also clicked
                };

                input.addEventListener("change", function () {
                    //getting user select file and [0] this means if user select multiple files then we'll select only the first one
                    files = this.files;
                    //  dropArea.classList.add("active");
                    showFile(); //calling function
                });

                //If user Drag File Over DropArea
                dropArea.addEventListener("dragover", (event) => {
                    event.preventDefault(); //preventing from default behaviour
                    //  dropArea.classList.add("active");
                    //   dragText.textContent = "Release to Upload File";
                });

                //If user leave dragged File from DropArea
                dropArea.addEventListener("dragleave", () => {
                    //  dropArea.classList.remove("active");
                    //   dragText.textContent = "Drag & Drop to Upload File";
                });

                //If user drop File on DropArea
                dropArea.addEventListener("drop", (event) => {
                    event.preventDefault(); //preventing from default behaviour
                    //getting user select file and [0] this means if user select multiple files then we'll select only the first one

                    files = event.dataTransfer.files;

                    showFile(); //calling function
                });

                function showFile() {
                    let file;
                    for (let i = 0; i < files.length; i++) {
                        file = files[i];

                        let fileType = file.type; //getting selected file type
                        let validExtensions = ["image/jpeg", "image/jpg", "image/png"]; //adding some valid image extensions in array
                        if(file.size < 5000000){
                          if (validExtensions.includes(fileType)) {
                            //if user selected file is an image file
                            let fileReader = new FileReader(); //creating new FileReader object
                            fileReader.onload = () => {
                                let fileURL = fileReader.result; //passing user file source in fileURL variable
                  
                                let imgItem = ` 
                                                    <img src="${fileURL}" />
                                              `;
                                photoItem.innerHTML = imgItem;
                                const photoItemTitle = dropArea.querySelector(".upload-image__title");
                                photoItemTitle.innerText = file.name
                            };
                            fileReader.readAsDataURL(file);
                            dropArea.classList.add("media_added");
                        } else {
                            alert("This is not an Image File!");
                            // dropArea.classList.remove("active");
                        }
                        } 
                        else{
                           alert("Image Size is too big. Should be less than 5mb!");
                        }
                    }
                   
                }

                // Delete Item
                removePhoto.addEventListener("click", deleteItem);

                function deleteItem(e) {
                    input.value = "";
                    dropArea.classList.contains("media_added") ? dropArea.classList.remove("media_added") : null;
                    button.click();
                }
            }
    }

    personalisation() {
      const personalisationBlock = document.querySelector("[data-option='Personalization']:not([data-limit])");
      const personalisationInput = personalisationBlock?.querySelector("#personalization_input");
      if (!personalisationBlock || !personalisationInput) return;

      const maxValueContainer = personalisationBlock.querySelector(".personalization-counter--max");
      const currentValueContainer = personalisationBlock.querySelector(".personalization-counter--current");

      const rootForm = document.querySelector(".shopify-product-form");

      /** "1", "2 letters", "10" → число для ліміту */
      const parseLettersCount = (raw) => {
        if (raw == null || raw === "") return NaN;
        const s = String(raw).trim();
        const m = s.match(/\d+/);
        if (!m) return NaN;
        const n = parseInt(m[0], 10);
        return Number.isFinite(n) && n > 0 ? n : NaN;
      };

      /** variant.option1 / option2 від теми Impulse (data-index="optionN") */
      const getLettersOptionIndexKey = () => {
        const wrap = document.querySelector(".option-how-many-letters");
        const el = wrap?.querySelector("[data-variant-input]");
        if (el?.dataset?.index) return el.dataset.index;
        const handleEls = rootForm?.querySelectorAll(".variant-input-wrap[data-handle]") || [];
        for (const fs of handleEls) {
          const h = fs.getAttribute("data-handle") || "";
          if (!/letter/i.test(h)) continue;
          const inp = fs.querySelector("[data-variant-input]");
          if (inp?.dataset?.index) return inp.dataset.index;
        }
        return null;
      };

      /** Поточне обране значення з DOM (радіо або select), без залежності від імені групи */
      const readMaxFromLettersDom = () => {
        const wrap = document.querySelector(".option-how-many-letters");
        const scope = wrap || rootForm;
        if (!scope) return NaN;

        const sel = wrap?.querySelector("select[data-variant-input]");
        if (sel && sel.value !== "") {
          return parseLettersCount(sel.value);
        }

        let groupName = null;
        const firstInWrap = wrap?.querySelector('input[type="radio"][data-variant-input]');
        if (firstInWrap?.name) groupName = firstInWrap.name;
        if (!groupName && rootForm) {
          for (const inp of rootForm.querySelectorAll('input[type="radio"][data-variant-input]')) {
            const fs = inp.closest(".variant-input-wrap[data-handle]");
            const h = fs?.getAttribute("data-handle") || "";
            if (/letter/i.test(h) || /letter/i.test(inp.name || "")) {
              groupName = inp.name;
              break;
            }
          }
        }
        if (!groupName && rootForm) {
          for (const inp of rootForm.querySelectorAll('input[type="radio"]')) {
            if (/letter/i.test(inp.name || "")) {
              groupName = inp.name;
              break;
            }
          }
        }
        if (!groupName) return NaN;

        const radios = [...scope.querySelectorAll('input[type="radio"]')].filter((r) => r.name === groupName);
        if (!radios.length) return NaN;
        const checked = radios.find((r) => r.checked);
        const val = checked != null ? checked.value : radios[0]?.value;
        return parseLettersCount(val);
      };

      const readMaxFromVariantDetail = (detail) => {
        const v = detail?.variant;
        if (!v) return NaN;
        const key = getLettersOptionIndexKey();
        if (key && v[key] != null) return parseLettersCount(v[key]);
        return NaN;
      };

      let lastValue = personalisationInput.value;

      const syncCounter = () => {
        if (currentValueContainer) {
          currentValueContainer.textContent = String(this.getCharacterQuantity(personalisationInput));
        }
      };

      let maxValue = 8;

      const applyMaxFromVariant = (evt) => {
        let n = readMaxFromVariantDetail(evt?.detail);
        if (!Number.isFinite(n) || n <= 0) n = readMaxFromLettersDom();
        if (!Number.isFinite(n) || n <= 0) n = maxValue;
        maxValue = n;
        if (maxValueContainer) maxValueContainer.textContent = String(maxValue);
        syncCounter();
      };

      applyMaxFromVariant();

      /** Клік по радіо "How many letters" */
      const onLettersControlChange = (e) => {
        const t = e.target;
        if (!t || !t.matches || !t.matches("[data-variant-input]")) return;
        const wrap = t.closest(".option-how-many-letters");
        const fs = t.closest(".variant-input-wrap[data-handle]");
        const handle = fs?.getAttribute("data-handle") || "";
        if (!wrap && !/letter/i.test(handle)) return;
        applyMaxFromVariant(e);
      };

      document.addEventListener("change", onLettersControlChange);

      /** Інша опція змінила варіант — тема виставляє checked без події change на радіо літер */
      const variantEvents = ["variant:change", "variantChange", "product:variant-change"];
      const onVariant = (e) => {
        applyMaxFromVariant(e);
        requestAnimationFrame(() => applyMaxFromVariant(e));
      };
      variantEvents.forEach((ev) => document.addEventListener(ev, onVariant));

      personalisationInput.addEventListener("input", () => {
        const currentCount = this.getCharacterQuantity(personalisationInput, personalisationInput.value);
        const previousCount = this.getCharacterQuantity(personalisationInput, lastValue);
        if (currentCount > maxValue && currentCount > previousCount) {
          personalisationInput.value = lastValue;
          syncCounter();
        } else {
          lastValue = personalisationInput.value;
          syncCounter();
        }
      });
    }

    isolatedPersonalisation() {
      const personalisationBlock = document.querySelector("[data-option='Personalization'][data-limit]");
      const personalisationInput = personalisationBlock?.querySelector("#personalization_input");
      if (!personalisationBlock || !personalisationInput) return;

      const maxValue = parseInt(personalisationBlock.getAttribute("data-limit"), 10) || 15;
      const currentValueContainer = personalisationBlock.querySelector(".personalization-counter--current");
      let lastValue = personalisationInput.value;

      const syncCounter = () => {
        if (currentValueContainer) {
          currentValueContainer.textContent = String(this.getCharacterQuantity(personalisationInput));
        }
      };

      syncCounter();

      personalisationInput.addEventListener("input", () => {
        const currentCount = this.getCharacterQuantity(personalisationInput, personalisationInput.value);
        const previousCount = this.getCharacterQuantity(personalisationInput, lastValue);
        if (currentCount > maxValue && currentCount > previousCount) {
          personalisationInput.value = lastValue;
          syncCounter();
        } else {
          lastValue = personalisationInput.value;
          syncCounter();
        }
      });
    }

    getCharacterQuantity(input, value = null) {
      if (!input) return 0;

      let text = value !== null ? value : input.value;
      text = text.trimEnd();
      text = text.replace(/\[([^\]]+)\]/g, "1");

      const segmenter = new Intl.Segmenter("uk", { granularity: "grapheme" });
      const segments = Array.from(segmenter.segment(text));
      const emojiRegex = /\p{Extended_Pictographic}/u;

      let emojiCount = 0;
      let charCount = 0;
      for (const { segment } of segments) {
        if (emojiRegex.test(segment)) {
          emojiCount++;
        } else if (segment.trim().length > 0) {
          charCount++;
        }
      }
      return emojiCount + charCount;
    }

    getCharacterCount(side, value = null) {
        const input = document.querySelector(
          side === "front" ? 'input[name="properties[Front engraving]"]' : 'input[name="properties[Back engraving]"]',
        );

        if (!input) return 0;

        let text = value !== null ? value : input.value;
        text = text.trimEnd();
        text = text.replace(/\[([^\]]+)\]/g, "1");

      // Створюємо сегментатор (працює в усіх сучасних браузерах)
      const segmenter = new Intl.Segmenter('uk', { granularity: 'grapheme' });
      const segments = Array.from(segmenter.segment(text));

      let emojiCount = 0;
      let charCount = 0;

      // Регулярний вираз для перевірки, чи є сегмент емодзі
      const emojiRegex = /\p{Extended_Pictographic}/u;

      for (const { segment } of segments) {
          if (emojiRegex.test(segment)) {
              emojiCount++;
          } else {
              // Якщо це не пробіл і не перенос рядка — рахуємо як символ
              if (segment.trim().length > 0) {
                  charCount++;
              }
          }
      }

      const finalCount = emojiCount + charCount

      return finalCount;
        // return text.length;
    }

    getCharacterCountSingle(inputItem, value = null) {
        const input = inputItem;

        if (!input) return 0;

        let text = value !== null ? value : input.value;
        text = text.trimEnd();
        text = text.replace(/\[([^\]]+)\]/g, "1");

      // Створюємо сегментатор (працює в усіх сучасних браузерах)
      const segmenter = new Intl.Segmenter('uk', { granularity: 'grapheme' });
      const segments = Array.from(segmenter.segment(text));

      let emojiCount = 0;
      let charCount = 0;

      // Регулярний вираз для перевірки, чи є сегмент емодзі
      const emojiRegex = /\p{Extended_Pictographic}/u;

      for (const { segment } of segments) {
          if (emojiRegex.test(segment)) {
              emojiCount++;
          } else {
              // Якщо це не пробіл і не перенос рядка — рахуємо як символ
              if (segment.trim().length > 0) {
                  charCount++;
              }
          }
      }

      const finalCount = emojiCount + charCount

      return finalCount;
        // return text.length;
    }


    /**
     * Feature 4.5: Sync Engraving Side checkboxes with hidden option
     */
    
    setupEngravingSideSync() {
      const backSideCheckbox = document.querySelector('.shopify-product-form input[value="Front Side + Back Side"]');
      if (!backSideCheckbox) return;

      // Try to find the hidden option selector (Radio or Select)
      const form_id = document.querySelector("div.shopify-product-form").dataset.form_id;
      const form = document.getElementById(form_id);
      if (!form) return;

      // Look for radio
      const radioBack = document.querySelector(`input[type="radio"][value="Front Side + Back Side"]`);
      const radioFront = document.querySelector(`input[type="radio"][value="Front Side"]`);

      // console.log("radioBack", radioBack);
      // console.log("radioFront", radioFront);

      const syncOption = (isChecked) => {
        if (radioBack && radioFront) {
          const target = isChecked ? radioBack : radioFront;
          target.checked = true;
          target.dispatchEvent(new Event("change", { bubbles: true }));
          target.click();
        }
      };

      // Initial sync if checked
      if (backSideCheckbox.checked) syncOption(true);

      backSideCheckbox.addEventListener("change", (e) => {
        syncOption(e.target.checked);
      });
    }

    /**
     * Feature 5: Selected swatch display without changing height
     */
    setupSelectedSwatchDisplay() {
      // Setup for Birthstone
      this.setupDisplayForOption("Birthstone", "radio");

      // Setup for Font
      this.setupDisplayForOption("Font", "radio");

      // Setup for Size
      this.setupDisplayForOption("Size", "radio");

      // Setup for Engraving Side
      this.updateEngravingSideDisplay();

      // Add CSS to prevent height changes
      this.addSwatchDisplayCSS();
    }

    setupDisplayForOption(optionName, inputType) {
      const optionSection = document.querySelector(`[data-option="${optionName}"]`);
      if (!optionSection) return;

      const labelContainer = optionSection.querySelector(".custom-options--label-container");
      if (!labelContainer) return;

      let selectedDisplay = labelContainer.querySelector(".custom-options--selected-swatch");
      if (!selectedDisplay) {
        selectedDisplay = document.createElement("div");
        selectedDisplay.className = "custom-options--selected-swatch";
        selectedDisplay.style.opacity = "0";
        selectedDisplay.style.height = "0";
        selectedDisplay.style.overflow = "hidden";
        selectedDisplay.style.transition = "opacity 0.2s ease, height 0.2s ease";
        labelContainer.appendChild(selectedDisplay);
      }

      const updateDisplay = () => {
        let selectedValue = "";

        if (inputType === "radio") {
          const checked = optionSection.querySelector(`input[name="properties[${optionName}]"]:checked`);
          if (checked) {
            const label = checked.closest("label");
            const valueSpan = label?.querySelector(".co_option_value");
            selectedValue = valueSpan ? valueSpan.innerHTML : checked.value;
          }
        }

        if (selectedValue) {
          selectedDisplay.innerHTML = `<p class="swatch_value custom-options--value">${selectedValue}</p>`;
          selectedDisplay.style.opacity = "1";
          selectedDisplay.style.height = "auto";
          selectedDisplay.style.marginTop = "8px";
        } else {
          selectedDisplay.style.opacity = "0";
          selectedDisplay.style.height = "0";
          selectedDisplay.style.marginTop = "0";
        }
      };

      const inputs = optionSection.querySelectorAll(`input[name="properties[${optionName}]"]`);
      inputs.forEach((input) => {
        input.addEventListener("change", updateDisplay);
      });

      updateDisplay();
    }

    updateEngravingSideDisplay() {
      const engravingSideSection = document.querySelector('[data-option="Engraving side"]');
      if (!engravingSideSection) return;

      const frontCheckbox = engravingSideSection.querySelector('input[value="Front Side"]');
      const backCheckbox = engravingSideSection.querySelector('input[value="Front Side + Back Side"]');
      const selectedDisplay = engravingSideSection.querySelector(".custom-options--selected-swatch");

      if (!selectedDisplay) return;

      const updateDisplay = () => {
        let displayText = "";

        if ((frontCheckbox && frontCheckbox.checked) && (backCheckbox && backCheckbox.checked)) {
          displayText = "Front Side<span>(Most Popular)</span> + Back Side<span>(Optional)</span>";
        } else if (frontCheckbox && frontCheckbox.checked) {
          displayText = "Front Side<span>(Most Popular)</span>";
        } else if (backCheckbox && backCheckbox.checked) {
          displayText = "Back Side<span>(Optional)</span>";
        }

        selectedDisplay.innerHTML = `<p class="swatch_value custom-options--value">${displayText}</p>`;
      };

      frontCheckbox ? frontCheckbox.addEventListener("change", updateDisplay) : null;
      backCheckbox ? backCheckbox.addEventListener("change", updateDisplay) : null;

      updateDisplay();
    }

    addSwatchDisplayCSS() {
      const style = document.createElement("style");
      style.textContent = `
        .custom-options--selected-swatch {
          transition: opacity 0.2s ease, height 0.2s ease, margin 0.2s ease;
        }
        
        .custom-options--selected-swatch p {
          margin: 0;
          padding: 6px 10px;
          background: #f5f5f5;
          border-radius: 4px;
          font-size: 13px;
          color: #666;
        }

        .custom-options--selected-swatch span {
          font-size: 11px;
          color: #999;
          margin-left: 4px;
        }

        .engraving-preview {
          animation: fadeIn 0.2s ease;
        }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-5px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .hide {
          display: none !important;
        }

        /* Symbol button active state */
        button.custom-options--option__value:active {
          transform: scale(0.95);
        }

        button.custom-options--option__value:hover {
          opacity: 0.8;
        }
      `;
      document.head.appendChild(style);
    }

    /**
     * Feature 6: Hijack Add to Cart event.
     *
     * The flow:
     *  1. Ignore the click if validation is still blocking (the validation
     *     handler has already called event.preventDefault()).
     *  2. Otherwise intercept the submit and build a FormData from the whole
     *     <form> — this includes the file input (properties[Image]) which
     *     JSON submission would drop — and strip empty `properties[...]`
     *     entries so we don't pollute the order with blanks.
     *  3. Post the main item to /cart/add.js using multipart/form-data.
     *  4. If the "Back Side" add-on is selected, post the second item in a
     *     follow-up JSON request (no file needed for the add-on).
     */
    setupAddToCartHijack() {
      const form_id = document.querySelector("div.shopify-product-form")?.dataset.form_id;
      const form = document.getElementById(form_id);
      if (!form) return;

      const submitButton = form.querySelector('button[type="submit"][name="add"]');
      if (!submitButton) return;

      submitButton.addEventListener("click", async (e) => {
        if (typeof this._necklaceApplyFieldErrorsAndGetValid === "function") {
          if (!this._necklaceApplyFieldErrorsAndGetValid()) {
            e.preventDefault();
            e.stopImmediatePropagation();
            submitButton.classList.add("required-fields");
            if (typeof this._necklaceScrollToFirstFormError === "function") {
              this._necklaceScrollToFirstFormError();
            }
            return;
          }
        }

        // Respect the validation gate — if the button still carries the guard
        // class, the click is either invalid or will be re-issued programmatically
        // after errors are painted. Don't hijack here.
        if (submitButton.classList.contains("required-fields")) return;

        e.preventDefault();
        e.stopImmediatePropagation();

        console.log("🛑 Add to Cart event hijacked");

        const rawFormData = new FormData(form);

        // Decide whether the shopper asked for the back-engraving bundle add-on.
        const engravingKey = "properties[Engraving side][]";
        const engravingValues = rawFormData.getAll(engravingKey);
        const hasFront = engravingValues.includes("Front Side");
        const hasBack = engravingValues.includes("Front Side + Back Side");

        let bundleId = null;
        if (hasBack) {
          bundleId = `bundle_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        }

        let engravingString = null;
        if (hasFront || hasBack) {
          engravingString = hasFront ? "Front Side" : "";
          if (hasBack) {
            let priceString = "";
            if (window.addOnProduct && window.addOnProduct.variants) {
              const variant =
                window.addOnProduct.variants.find((v) => v.available) || window.addOnProduct.variants[0];
              if (variant) priceString = ` (+ $${(variant.price / 100).toFixed(2)})`;
            }
            engravingString += (hasFront ? " + " : "") + "Back Side" + priceString;
          }
        }

        // Rebuild the FormData so we can:
        //  - skip the raw `Engraving side[]` entries (we replace them with the string version)
        //  - skip empty string properties
        //  - skip empty file inputs (no file chosen)
        //  - keep everything else (id, quantity, selling_plan, properties[Image], ...)
        const mainFormData = new FormData();
        mainFormData.append("form_type", "product");
        mainFormData.append("utf8", "✓");

        for (const [key, value] of rawFormData.entries()) {
          if (key === engravingKey) continue;

          if (key.startsWith("properties[")) {
            if (value instanceof File) {
              if (!value.name || value.size === 0) continue;
              mainFormData.append(key, value, value.name);
              continue;
            }
            if (typeof value === "string" && value.trim() === "") continue;
            mainFormData.append(key, value);
          } else {
            // id / quantity / selling_plan / section — pass through verbatim
            mainFormData.append(key, value);
          }
        }

        if (engravingString) mainFormData.append("properties[Engraving side]", engravingString);
        if (bundleId) mainFormData.append("properties[_bundleId]", bundleId);

        submitButton.classList.add("btn--loading");
        try {
          // Browser will set Content-Type: multipart/form-data automatically with the correct boundary.
          const response = await fetch("/cart/add.js", {
            method: "POST",
            body: mainFormData,
          });
          const data = await response.json();

          if (!response.ok) {
            console.error("❌ Error adding main item to cart:", data);
            alert(data.description || "Error adding to cart");
            return;
          }
          console.log("✅ Main item added to cart:", data);

          // Add-on (Back side) — optional second call, JSON is fine (no file).
          if (bundleId && window.addOnProduct && window.addOnProduct.variants) {
            const firstAvailable = window.addOnProduct.variants.find((v) => v.available);
            if (firstAvailable) {
              const addonResp = await fetch("/cart/add.js", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  items: [
                    {
                      id: firstAvailable.id,
                      quantity: 1,
                      properties: { _bundleId: bundleId },
                    },
                  ],
                }),
              });
              const addonData = await addonResp.json();
              if (!addonResp.ok) console.warn("⚠️ Add-on not added:", addonData);
              else console.log("✅ Add-on item added to cart:", addonData);
            } else {
              console.warn("Add-on product has no available variants.");
            }
          }

          document.dispatchEvent(new CustomEvent("ajaxProduct:added"));
        } catch (error) {
          console.error("❌ Network error:", error);
        } finally {
          submitButton.classList.remove("btn--loading");
        }
      });

      console.log("✅ Add to Cart Hijack enabled");
    }

    /**
     * Feature 7: Price Observer
     */
    setupPriceObserver() {
      // 1. Get initial variant price
      if (window.product && window.product.variants) {
        let variantId = null;

        // Try form input first
        const form_id = document.querySelector("div.shopify-product-form")?.dataset.form_id;
        const form = document.getElementById(form_id);
        const idInput = form?.querySelector('[name="id"]');
        if (idInput) {
          variantId = idInput.value;
        }

        let variant;
        if (variantId) {
          variant = window.product.variants.find((v) => v.id == variantId);
        }

        if (!variant) {
          variant = window.product.variants.find((v) => v.available) || window.product.variants[0];
        }

        if (variant) {
          this.currentBasePrice = variant.price;
        }
      }

      // 2. Listen for variant changes (Impulse theme event)
      document.addEventListener("variant:change", (e) => {
        if (e.detail.variant) {
          this.currentBasePrice = e.detail.variant.price;

          // Sync checkbox state if variant options dictate it
          const variant = e.detail.variant;
          const backSideCheckbox = document.querySelector('.shopify-product-form input[value="Front Side + Back Side"]');

          if (backSideCheckbox) {
            if (variant.options.includes("Front Side")) {
              if (backSideCheckbox.checked) {
                backSideCheckbox.checked = false;
                backSideCheckbox.dispatchEvent(new Event("change"));
              }
            } else if (variant.options.includes("Front Side + Back Side")) {
              if (!backSideCheckbox.checked) {
                backSideCheckbox.checked = true;
                backSideCheckbox.dispatchEvent(new Event("change"));
              }
            }
          }

          // Delay to ensure we override theme.js default price update
          setTimeout(() => {
            this.updatePriceDisplay();
          }, 50);
        }
      });

      // Initial update
      this.updatePriceDisplay();
    }

    updatePriceDisplay() {
      let finalPrice = this.currentBasePrice;

      const priceElements = document.querySelectorAll(
        ".price__regular .price-item--regular, .product__price:not(.product__price--compare), .price-item--regular",
      );

      if (window.theme && window.theme.Currency && window.theme.settings) {
        const formattedPrice = theme.Currency.formatMoney(finalPrice, theme.settings.moneyFormat);
        priceElements.forEach((el) => (el.innerHTML = formattedPrice));
      }
    }

    setupStoneUnselect() {
      // There are multiple [data-stone] containers on the page:
      //  - original Birthstone (with a related Dog Breed preview)
      //  - the State slider (uses <button> not radio — forEach below is a no-op)
      //  - per-side Birthstones inside each .sides-block
      // Each must keep its own lastSelected pointer so clicking a radio in one
      // container doesn't "unselect" a different container's previously-chosen radio.
      const stoneContainers = document.querySelectorAll("[data-stone]");
      if (!stoneContainers.length) return;

      stoneContainers.forEach((stoneContainer) => {
        const radioInputs = stoneContainer.querySelectorAll('input[type="radio"]');
        if (!radioInputs.length) return;

        // Dog breed preview lives only next to the original Birthstone block
        const scopedBreedContainer = stoneContainer.closest('[data-option="Dog Breed"]');
        const breedPreview = scopedBreedContainer?.querySelector(".breed_preview");

        let lastSelected = null;
        radioInputs.forEach((radio) => {
          radio.addEventListener("click", () => {
            if (lastSelected === radio && radio.checked) {
              radio.checked = false;
              lastSelected = null;
              radio.dispatchEvent(new Event("change", { bubbles: true }));
              if (breedPreview) breedPreview.innerText = "";
              console.log("🔘 Birthstone unselected:", radio.name);
            } else {
              lastSelected = radio;
              if (breedPreview) breedPreview.innerText = radio.value;
            }
          });
        });
      });

      console.log("✅ Radio unselect enabled for all [data-stone] blocks");
    }

    setupSymbolsBlocks() {
      const symbolSections = document.querySelectorAll('[data-symbol]');
      symbolSections.forEach((symbolSection)=>{

          const symbolMaxCount = parseInt(symbolSection.getAttribute("data-symbol")) || 10;
          const engravingSection = symbolSection.querySelector('[data-option]');
          const engravingInput = symbolSection.querySelector('input');

          if (!symbolSection || !engravingInput || !engravingSection) return;

          // Create preview element after the input
          let previewElement = engravingSection.querySelector(".engraving-preview");
          if (!previewElement) {
            previewElement = document.createElement("div");
            previewElement.className = "engraving-preview";
            previewElement.style.cssText = `
              margin-top: 12px;
              padding: 12px;
              background: #f8f8f8;
              border: 1px solid #e0e0e0;
              border-radius: 6px;
              min-height: 50px;
              display: flex;
              align-items: center;
              gap: 6px;
              flex-wrap: wrap;
              font-size: 16px;
              line-height: 1.5;
            `;

            const inputContainer = symbolSection.querySelector(".custom-options__engraving-input");
            inputContainer.after(previewElement);
          }

          // Create character counter in help text
          let helpText = symbolSection.querySelector(".custom-options--helptext-content");
          if (!helpText) {
            helpText = document.createElement("div");
            helpText.className = "custom-options--helptext-content";
            symbolSection.appendChild(helpText);
          }

          const updateCharacterCount = () => {
            const count = this.getCharacterCountSingle(engravingInput);
            helpText.innerHTML = `<p>${count}/${symbolMaxCount}</p>`;

            if (count >= symbolMaxCount) {
              helpText.style.color = "#d32f2f";
            } else {
              helpText.style.color = "";
            }
          };

          // Get all symbol buttons
          const symbolButtons = symbolSection.querySelectorAll("button[data-target]");

          // Populate symbol map
          symbolButtons.forEach((button, index) => {
            const symbolImage = button.querySelector(".custom-options--option__image-value");
            const symbolName = button.querySelector(".co_option_value")?.textContent || `symbol-${index + 1}`;
            const imageUrl = symbolImage?.style.backgroundImage.match(/url\("?(.+?)"?\)/)?.[1];
            if (symbolName && imageUrl) {
              this.symbolMapSingle[symbolName] = imageUrl;
            }
          });

          let lastValue = engravingInput.value;

          // Handle symbol button clicks
          symbolButtons.forEach((button, index) => {
            button.addEventListener("click", (e) => {
              e.preventDefault();

              const symbolName = button.querySelector(".co_option_value")?.textContent || `symbol-${index + 1}`;
            
              if (!this.symbolMapSingle[symbolName]) return;

              // Get current cursor position
              const cursorPos = engravingInput.selectionStart || engravingInput.value.length;
              const currentValue = engravingInput.value;

              // Check character limit
              if (this.getCharacterCountSingle(engravingInput) >= symbolMaxCount) {
                alert(`Maximum ${symbolMaxCount} characters reached`);
                return;
              }

              // Insert symbol tag at cursor position
              const symbolTag = `[${symbolName}]`;
              const before = currentValue.substring(0, cursorPos);
              const after = currentValue.substring(cursorPos);
              engravingInput.value = before + symbolTag + after;
              engravingInput.dispatchEvent(new Event("input", { bubbles: true }));

              // Update preview
              this.updatePreviewSingle(engravingInput, previewElement);
              updateCharacterCount();

              // Set cursor after symbol
              engravingInput.selectionStart = engravingInput.selectionEnd = cursorPos + symbolTag.length;
              engravingInput.focus();

              // console.log(`✨ Symbol added: ${symbolName}`);
            });
          });

          // Handle text input changes
          engravingInput.addEventListener("input", (e) => {
            const currentCount = this.getCharacterCountSingle(engravingInput, engravingInput.value);
            const previousCount = this.getCharacterCountSingle(engravingInput, lastValue);

            if (currentCount > symbolMaxCount && currentCount > previousCount) {
              engravingInput.value = lastValue;
            } else {
              lastValue = engravingInput.value;
            }

            this.updatePreviewSingle(engravingInput, previewElement);
            updateCharacterCount();
          });

          // Handle backspace to remove symbols
          engravingInput.addEventListener("keydown", (e) => {
            if (e.key === "Backspace") {
              const cursorPos = engravingInput.selectionStart;
              const currentValue = engravingInput.value;

              if (cursorPos > 0) {
                const beforeCursor = currentValue.substring(0, cursorPos);
                // Check if we are deleting the closing bracket of a symbol
                const match = beforeCursor.match(/\[([^\]]+)\]$/);

                if (match) {
                  const symbolName = match[1];
                  if (this.symbolMapSingle[symbolName]) {
                    e.preventDefault();
                    // Remove the whole tag
                    const newValue = currentValue.substring(0, cursorPos - match[0].length) + currentValue.substring(cursorPos);
                    engravingInput.value = newValue;
                    engravingInput.selectionStart = engravingInput.selectionEnd = cursorPos - match[0].length;
                    engravingInput.dispatchEvent(new Event("input", { bubbles: true }));

                    this.updatePreviewSingle(engravingInput, previewElement);
                    updateCharacterCount();
                  }
                }
              }
            }
          });

          // Initial update
          updateCharacterCount();
        })
    }

    displayBirthstoneBar(){
      const charms = document.querySelector(".option-how-many-charms");
      if(charms){
         const selectedValue = parseInt(charms.querySelector("input:checked").value);
          const charmBlocks = document.querySelectorAll("[data-option='Charms'] .custom-options--block");
          charmBlocks.forEach((charmBlock, index)=>{
            if(index + 1 <= selectedValue){
              charmBlock.classList.contains("custom-options--block-blocked") ? charmBlock.classList.remove("custom-options--block-blocked") : null
            } else{
              !charmBlock.classList.contains("custom-options--block-blocked") ? charmBlock.classList.add("custom-options--block-blocked") : null
            }
          })
            charms.addEventListener("click", function(e){
              if(e.target.closest("input")){
                const selectedValue = parseInt(e.target.closest("input").value);
                charmBlocks.forEach((charmBlock, index)=>{
                  if(index + 1 <= selectedValue){
                    charmBlock.classList.contains("custom-options--block-blocked") ? charmBlock.classList.remove("custom-options--block-blocked") : null
                  } else{
                    !charmBlock.classList.contains("custom-options--block-blocked") ? charmBlock.classList.add("custom-options--block-blocked") : null
                  }
            })
            } 
          })
      }
    }
    displayBirthstoneBarStones(){
      const charms = document.querySelector(".option-how-many-stones");
      if(charms){
         const selectedValue = parseInt(charms.querySelector("input:checked").value);
          const charmBlocks = document.querySelectorAll("[data-option='Stones'] .custom-options--block");
          charmBlocks.forEach((charmBlock, index)=>{
            if(index + 1 <= selectedValue){
              charmBlock.classList.contains("custom-options--block-blocked") ? charmBlock.classList.remove("custom-options--block-blocked") : null
            } else{
              !charmBlock.classList.contains("custom-options--block-blocked") ? charmBlock.classList.add("custom-options--block-blocked") : null
            }
          })
            charms.addEventListener("click", function(e){
              if(e.target.closest("input")){
                const selectedValue = parseInt(e.target.closest("input").value);
                charmBlocks.forEach((charmBlock, index)=>{
                  if(index + 1 <= selectedValue){
                    charmBlock.classList.contains("custom-options--block-blocked") ? charmBlock.classList.remove("custom-options--block-blocked") : null
                  } else{
                    !charmBlock.classList.contains("custom-options--block-blocked") ? charmBlock.classList.add("custom-options--block-blocked") : null
                  }
            })
            } 
          })
      }
    }

    /**
     * Feature 9: Progressive Sides Selection
     *
     * Architecture:
     *  - Shopify product option "How many sides" (values 1 / 2 / 3 / 4 — or
     *    "1 Side", "2 Sides", … any string starting with a digit) drives both
     *    pricing (native Shopify variants) and visibility of side-blocks.
     *  - Side names are hardcoded in the snippet: Front, Right, Back, Left
     *    (1 → Front only, 2 → Front+Right, 3 → Front+Right+Back, 4 → all).
     *  - Each side block has the standard [data-symbol] / [data-stone]
     *    structure, so it is automatically wired by setupSymbolsBlocks() and
     *    setupStoneUnselect() — no per-side logic is needed.
     *  - We just (un)blur blocks via the .custom-options--block-blocked class.
     */
    displaySidesBlocks() {
      const sidesContainer = document.querySelector(".sides-selection");
      if (!sidesContainer) return;

      const sideBlocks = Array.from(sidesContainer.querySelectorAll(".sides-block"));
      if (!sideBlocks.length) return;

      // Pill-checkboxes UI — now independent. User picks any combination of
      // sides; the Shopify variant option ("How many sides" = 1/2/3/4) is
      // derived automatically from the number of checked pills (for pricing),
      // while properties[Sides] stores the chosen labels ("Right Side, Left Side").
      const pickerPills = Array.from(sidesContainer.querySelectorAll(".sides-picker__pill"));
      const pickerCheckboxes = Array.from(sidesContainer.querySelectorAll(".sides-picker__checkbox"));
      const pickerSummaryCount = sidesContainer.querySelector(".sides-picker-summary__count");
      const pickerSummaryPlural = sidesContainer.querySelector(".sides-picker-summary__plural");
      const sidesProperty = sidesContainer.querySelector("#co_sides_property");

      // Hidden Shopify variant selector (drives price). Values: 1/2/3/4.
      const sidesSelector =
        document.querySelector(".option-how-many-sides") ||
        document.querySelector('[class*="option-"][class*="-sides"]');

      // -------- helpers --------

      // Read currently checked pill indices (1-based, in pill order).
      const getCheckedIndices = () => {
        const result = [];
        pickerCheckboxes.forEach((cb, i) => {
          if (cb.checked) result.push(i + 1);
        });
        return result;
      };

      // Sync visibility of per-side blocks: show only the ones whose
      // data-side-index is in the selected set; clear hidden sides' values.
      const applyVisibility = (selectedSet) => {
        sideBlocks.forEach((block) => {
          const idx = parseInt(block.dataset.sideIndex, 10);
          const isVisible = selectedSet.has(idx);
          block.classList.toggle("custom-options--block-blocked", !isVisible);

          if (!isVisible) {
            block.querySelectorAll('input[type="text"]').forEach((i) => {
              if (i.value) {
                i.value = "";
                i.dispatchEvent(new Event("input", { bubbles: true }));
              }
            });
            block.querySelectorAll('input[type="radio"]:checked').forEach((i) => {
              i.checked = false;
              i.dispatchEvent(new Event("change", { bubbles: true }));
            });
            const preview = block.querySelector(".engraving-preview");
            if (preview) {
              preview.innerHTML = "";
              preview.style.display = "none";
            }
          }
        });
      };

      // Update summary, pill classes and the hidden properties[Sides] value.
      const syncPickerUI = (checkedIndices) => {
        pickerCheckboxes.forEach((cb, i) => {
          const isActive = cb.checked;
          pickerPills[i]?.classList.toggle("is-checked", isActive);
        });
        const labels = checkedIndices.map((idx) => pickerCheckboxes[idx - 1]?.dataset.sideLabel || "").filter(Boolean);
        if (pickerSummaryCount) pickerSummaryCount.textContent = String(labels.length);
        if (pickerSummaryPlural) pickerSummaryPlural.textContent = labels.length === 1 ? "" : "s";
        if (sidesProperty) sidesProperty.value = labels.join(", ");
      };

      // Set the Shopify variant radio matching the given count (1..4).
      const setShopifyVariantByCount = (count) => {
        if (!sidesSelector) return false;
        const radios = sidesSelector.querySelectorAll('input[type="radio"]');
        const target = Array.from(radios).find((r) => parseInt(r.value, 10) === count);
        if (!target) return false;
        if (target.checked) return true;
        target.checked = true;
        target.dispatchEvent(new Event("change", { bubbles: true }));
        try { target.click(); } catch (err) { /* noop */ }
        return true;
      };

      // Central render — call after any change in pill checked state.
      const render = (opts = {}) => {
        let checked = getCheckedIndices();

        // Guard: at least one side must be selected. If the user just
        // unchecked the last active one, restore it.
        if (checked.length === 0) {
          const fallbackIdx = opts.restoreIndex || 1;
          pickerCheckboxes[fallbackIdx - 1].checked = true;
          checked = [fallbackIdx];
        }

        const selectedSet = new Set(checked);
        applyVisibility(selectedSet);
        syncPickerUI(checked);

        if (opts.syncShopify !== false) {
          setShopifyVariantByCount(checked.length);
        }
      };

      // -------- handlers --------

      // Pill click — toggle the corresponding checkbox, then re-render.
      // We intercept the label click so we can keep the "at least 1" invariant.
      pickerPills.forEach((pill) => {
        pill.addEventListener("click", (e) => {
          // Click on the label (outside the native input) — the <input> click
          // will bubble up too, so we only handle the one originating from
          // the label's pointer area (not the checkbox itself) to avoid
          // double-toggling.
          if (e.target && e.target.closest(".sides-picker__checkbox")) return;
          e.preventDefault();
          const index = parseInt(pill.dataset.sideIndex, 10);
          if (!index) return;
          const cb = pickerCheckboxes[index - 1];
          if (!cb) return;

          const wasChecked = cb.checked;
          cb.checked = !wasChecked;

          render({ restoreIndex: index });
        });
      });

      // Native checkbox change (keyboard / AT users) — just re-render.
      pickerCheckboxes.forEach((cb) => {
        cb.addEventListener("change", () => {
          render({ restoreIndex: parseInt(cb.dataset.sideIndex, 10) });
        });
      });

      // React to external variant changes (e.g. the size option forces a
      // different "how many sides" value). We only re-sync the Shopify side —
      // we don't touch the user's pill selection in that case, because the
      // merchant has probably configured a specific layout for that variant.
      document.addEventListener("variant:change", () => {
        // Keep pills as is; the variant price is handled by the theme.
      });

      // Initial render: honour pre-checked pills from Liquid (Front by default).
      // If nothing is pre-checked, fall back to Front.
      if (getCheckedIndices().length === 0 && pickerCheckboxes[0]) {
        pickerCheckboxes[0].checked = true;
      }
      render();

      console.log("✅ Sides independent picker enabled");
    }

    /**
     * Feature 8: State Selection + Initials (necklace personalisation)
     *
     * Behavior:
     *  - Two visible "slots" (State 1 / State 2) above a slider of states
     *  - Clicking a state in the slider fills slot 1 first, then slot 2
     *  - When both slots are filled, subsequent clicks replace slot 1, then slot 2 (alternating)
     *  - Initials input is locked until both states are picked.
     *    If the user tries to focus/click it earlier — both empty slots flash red
     *  - Initials are forced to lowercase and capped at 2 characters
     *  - Preview = [State1 icon] + letter1 + ♥ + [State2 icon] + letter2 (light gray bg)
     *  - Hidden field properties[Necklace] holds the text format saved at checkout:
     *      "[Alabama] A ♥ [California] C"
     */
    setupStateSelection() {
      const stateContainer = document.querySelector('.state-selection[data-option="State"]');
      if (!stateContainer) return;

      const stateButtons = stateContainer.querySelectorAll(".state-option");
      const slots = [
        stateContainer.querySelector('.state-slot[data-slot="1"]'),
        stateContainer.querySelector('.state-slot[data-slot="2"]'),
      ];
      const slotInputs = [
        stateContainer.querySelector("#co_state_1"),
        stateContainer.querySelector("#co_state_2"),
      ];
      const initialsBlock = stateContainer.querySelector(".state-initials-block");
      const initialsInput = stateContainer.querySelector(".state-initials-input");
      const initialsCounter = stateContainer.querySelector(".state-initials-counter__current");
      const previewElement = stateContainer.querySelector(".state-preview");
      const checkoutInput = stateContainer.querySelector("#co_state_necklace");

      if (!stateButtons.length || !slots[0] || !slots[1] || !initialsInput) return;

      // Per-slot data we keep in JS (kept in sync with hidden inputs)
      const slotData = [
        { name: "", abr: "", img: "" },
        { name: "", abr: "", img: "" },
      ];

      // When both slots are filled and the user picks another state,
      // replace slot 1 first, then slot 2, then loop.
      let nextReplaceSlot = 0; // 0 → slot 1, 1 → slot 2

      const heartSVG = `
        <svg class="state-preview__heart" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
          <path d="M12 21s-7.5-4.6-9.5-9.4C1 7.9 3.6 4 7.5 4c2 0 3.6 1 4.5 2.5C12.9 5 14.5 4 16.5 4 20.4 4 23 7.9 21.5 11.6 19.5 16.4 12 21 12 21z"
                fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>
        </svg>
      `;

      const renderSlots = () => {
        slots.forEach((slot, i) => {
          const data = slotData[i];
          const labelEl = slot.querySelector(".state-slot__label");
          if (data.name) {
            slot.classList.add("filled");
            labelEl.textContent = data.name;
          } else {
            slot.classList.remove("filled");
            labelEl.textContent = i === 0 ? "State 1" : "State 2";
          }
        });

        // highlight selected buttons in slider
        stateButtons.forEach((btn) => {
          const n = btn.dataset.stateName;
          const isSelected = n === slotData[0].name || n === slotData[1].name;
          btn.classList.toggle("selected", isSelected);
        });
      };

      const renderInitialsLock = () => {
        const bothPicked = !!slotData[0].name && !!slotData[1].name;
        initialsBlock.classList.toggle("locked", !bothPicked);
        initialsInput.disabled = !bothPicked;
      };

      const updateCheckoutValue = () => {
        const initials = (initialsInput.value || "").toLowerCase();
        const c1 = initials[0] || "";
        const c2 = initials[1] || "";
        if (slotData[0].name && slotData[1].name) {
          checkoutInput.value =
            `[${slotData[0].name}] ${c1.toUpperCase()} \u2665 [${slotData[1].name}] ${c2.toUpperCase()}`.trim();
        } else {
          checkoutInput.value = "";
        }
      };

      const renderPreview = () => {
        const initials = (initialsInput.value || "").toLowerCase();
        const c1 = initials[0] || "";
        const c2 = initials[1] || "";
        const hasAny = slotData[0].name || slotData[1].name;

        if (!hasAny) {
          previewElement.innerHTML = "";
          previewElement.style.display = "none";
          return;
        }

        previewElement.style.display = "flex";

        let html = "";
        if (slotData[0].img) {
          html += `<img src="${slotData[0].img}" alt="${slotData[0].name}" class="state-preview__icon">`;
        }
        if (c1) html += `<span class="state-preview__letter">${c1}</span>`;
        html += heartSVG;
        if (slotData[1].img) {
          html += `<img src="${slotData[1].img}" alt="${slotData[1].name}" class="state-preview__icon">`;
        }
        if (c2) html += `<span class="state-preview__letter">${c2}</span>`;

        previewElement.innerHTML = html;
      };

      const writeSlot = (index, data) => {
        slotData[index] = { ...data };
        slotInputs[index].value = data.name || "";
        slotInputs[index].dispatchEvent(new Event("change", { bubbles: true }));
      };

      const clearSlotErrors = () => {
        slots.forEach((s) => s.classList.remove("error"));
      };

      const flashSlotErrors = () => {
        slots.forEach((s, i) => {
          if (!slotData[i].name) {
            s.classList.add("error");
          }
        });
      };

      const pickState = (data) => {
        // Clicking an already-selected state → unselect it from its slot
        const existingIndex = slotData.findIndex((s) => s.name === data.name);
        if (existingIndex !== -1) {
          writeSlot(existingIndex, { name: "", abr: "", img: "" });
          // After unselecting, that slot becomes the next target
          nextReplaceSlot = existingIndex;
        } else if (!slotData[0].name) {
          writeSlot(0, data);
          nextReplaceSlot = 0;
        } else if (!slotData[1].name) {
          writeSlot(1, data);
          nextReplaceSlot = 0;
        } else {
          // both filled → replace; slot 1 first, then slot 2, alternating
          writeSlot(nextReplaceSlot, data);
          nextReplaceSlot = nextReplaceSlot === 0 ? 1 : 0;
        }

        clearSlotErrors();
        renderSlots();
        renderInitialsLock();
        renderPreview();
        updateCheckoutValue();

        // Remove any "error" state on the parent stack
        const stack = stateContainer.closest(".custom-options--stack.error");
        if (stack) stack.classList.remove("error");
      };

      stateButtons.forEach((btn) => {
        btn.addEventListener("click", (e) => {
          e.preventDefault();
          pickState({
            name: btn.dataset.stateName || "",
            abr: btn.dataset.stateAbr || "",
            img: btn.dataset.stateImg || "",
          });
        });
      });

      // --- Initials input behaviour ---
      const tryUnlockOrFlash = (e) => {
        if (slotData[0].name && slotData[1].name) return; // unlocked, OK
        // Block interaction and flash the empty slots
        e.preventDefault();
        flashSlotErrors();
        initialsInput.blur();
      };

      // Block interactions when locked. We use both pointerdown and focus so
      // mouse, touch and keyboard tab attempts all trigger the warning.
      initialsInput.addEventListener("pointerdown", tryUnlockOrFlash);
      initialsInput.addEventListener("focus", (e) => {
        if (!slotData[0].name || !slotData[1].name) {
          flashSlotErrors();
          initialsInput.blur();
        }
      });

      initialsInput.addEventListener("input", () => {
        // Keep only letters, lowercase, max 2 chars
        let v = (initialsInput.value || "").toLowerCase().replace(/[^a-zа-яёіїєґ]/giu, "");
        if (v.length > 2) v = v.slice(0, 2);
        if (v !== initialsInput.value) initialsInput.value = v;
        if (initialsCounter) initialsCounter.textContent = String(v.length);
        renderPreview();
        updateCheckoutValue();
      });

      // Initial render
      renderSlots();
      renderInitialsLock();
      renderPreview();
      updateCheckoutValue();

      console.log("✅ State selection enabled");
    }

    updatePreview(side, input, previewElement) {
      const value = input.value.trimEnd(); // Trim trailing spaces
      previewElement.innerHTML = "";

      if (!value) {
        previewElement.style.display = "none";
        return;
      }

      previewElement.style.display = "flex";

      // Split by symbol tags
      const parts = value.split(/(\[[^\]]+\])/);

      let previewHTML = "";
      parts.forEach((part) => {
        const match = part.match(/^\[([^\]]+)\]$/);
        if (match && this.symbolMap[side][match[1]]) {
          const symbolName = match[1];
          const imageUrl = this.symbolMap[side][symbolName];
          previewHTML += `<img src="${imageUrl}" alt="${symbolName}" style="width: 24px; height: 24px; object-fit: contain; margin: 0 2px;">`;
        } else if (part) {
          previewHTML += `<span>${part}</span>`;
        }
      });

      previewElement.innerHTML = previewHTML;
    }
    updatePreviewSingle( input, previewElement) {
      const value = input.value.trimEnd(); // Trim trailing spaces
      previewElement.innerHTML = "";

      if (!value) {
        previewElement.style.display = "none";
        return;
      }

      previewElement.style.display = "flex";

      // Split by symbol tags
      const parts = value.split(/(\[[^\]]+\])/);

      let previewHTML = "";
      parts.forEach((part) => {
        const match = part.match(/^\[([^\]]+)\]$/);
        if (match && this.symbolMapSingle[match[1]]) {
          const symbolName = match[1];
          const imageUrl = this.symbolMapSingle[symbolName];
          previewHTML += `<img src="${imageUrl}" alt="${symbolName}" style="width: 24px; height: 24px; object-fit: contain; margin: 0 2px;">`;
        } else if (part) {
          previewHTML += `<span>${part}</span>`;
        }
      });

      previewElement.innerHTML = previewHTML;
    }
  }


  // Initialize
  new CustomProductOptions();
})();
